import { TFunction } from "react-i18next";
import { AlertsApi } from "src/services/Api/Organization/types";
import { FILTER_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { DashboardAlertRowProps } from "../components/DashboardAlertSection/DashboardAlertRow";

export const convertAlertToDisplayData = (
	key: keyof AlertsApi,
	alertsData: AlertsApi,
	organizationId: number | string,
	t: TFunction
): DashboardAlertRowProps | null => {
	switch (key) {
		case "open_products_count":
			return {
				title: t("modules.dashboard.alerts.open_products_count.title"),
				value: alertsData.open_products_count,
			};
		case "unlinked_products_count": {
			const filter = btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}${FILTER_SEPARATOR}resource_type|e=PRODUCT${FILTER_SEPARATOR}status|e=OPEN${FILTER_SEPARATOR}used|true`
					)
				)
			);

			return {
				title: t("modules.dashboard.alerts.unlinked_products_count.title"),
				value: alertsData.unlinked_products_count,
				link: `/${organizationId}/apps/linking_products?f=${filter}`,
			};
		}
		case "unlinked_stocks_count": {
			const filter = btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}${FILTER_SEPARATOR}resource_type|e=STOCK${FILTER_SEPARATOR}status|e=OPEN${FILTER_SEPARATOR}used|true`
					)
				)
			);

			return {
				title: t("modules.dashboard.alerts.unlinked_stocks_count.title"),
				value: alertsData.unlinked_stocks_count,
				link: `/${organizationId}/apps/linking_stocks?f=${filter}`,
			};
		}
		case "unlinked_sales_order_count": {
			const filter = btoa(
				unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}${FILTER_SEPARATOR}statuses|e=UNLINKED`))
			);

			return {
				title: t("modules.dashboard.alerts.unlinked_sales_order_count.title"),
				value: alertsData.unlinked_sales_order_count,
				link: `/${organizationId}/documents/sales?f=${filter}`,
			};
		}
		case "products_below_critical_amount": {
			const filter = btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}${FILTER_SEPARATOR}critical_amount|e=in_stock`
					)
				)
			);

			return {
				title: t("modules.dashboard.alerts.products_below_critical_amount.title"),
				value: alertsData.products_below_critical_amount,
				link: `/${organizationId}/states?f=${filter}`,
			};
		}
		case "unlinked_purchase_order_products_count": {
			const filter = btoa(
				unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}${FILTER_SEPARATOR}statuses|e=UNLINKED`))
			);

			return {
				title: t("modules.dashboard.alerts.unlinked_purchase_order_products_count.title"),
				value: alertsData.unlinked_purchase_order_products_count,
				link: `/${organizationId}/documents/purchases?f=${filter}`,
			};
		}
		case "last_calculate_job_at_minutes_ago": {
			return {
				title: t("modules.dashboard.alerts.last_calculate_job_at_minutes_ago.title"),
				value: alertsData.last_calculate_job_at_minutes_ago
					? alertsData.last_calculate_job_at_minutes_ago +
					  " " +
					  t("modules.dashboard.alerts.last_calculate_job_at_minutes_ago.value")
					: undefined,
			};
		}
		case "error_channels":
			return {
				title: t("modules.dashboard.alerts.error_channels.title"),
				value: alertsData.error_channels?.length,
			};
		default:
			return null;
	}
};

export const convertAlertsToDisplayData = (
	alertsData: AlertsApi,
	organizationId: number | string,
	t: TFunction
): DashboardAlertRowProps[] => {
	if (!alertsData) {
		return [];
	}

	const sortedAlertsKeys = sortAlertsDisplayData(Object.keys(alertsData) as (keyof AlertsApi)[]);

	const displayData = sortedAlertsKeys
		.map((alertKey) => convertAlertToDisplayData(alertKey, alertsData, organizationId, t))
		.filter((alert) => alert !== null) as DashboardAlertRowProps[];

	return displayData;
};

const sortAlertsDisplayData = (alertsKeys: (keyof AlertsApi)[]): (keyof AlertsApi)[] => {
	const alertsOrder: (keyof AlertsApi)[] = [
		"open_products_count",
		"unlinked_products_count",
		"unlinked_stocks_count",
		"unlinked_sales_order_count",
		"error_channels",
		"products_below_critical_amount",
		"unlinked_purchase_order_products_count",
		"last_calculate_job_at_minutes_ago",
	];

	return alertsOrder.filter((alertKey) => alertsKeys.includes(alertKey));
};
